const STORAGE_PREFIX = 'easy-dialog-seen-'
const SYNC_REQUEST_KEY = 'easy-dialog-sync-request'
const SYNC_RESPONSE_KEY = 'easy-dialog-sync-response'
const SYNC_WAIT_MS = 150
const TAB_ID = `${Date.now()}-${Math.random().toString(16).slice(2)}`
const dialogOpeners = new WeakMap()

function getDialogRoot() {
	return document.getElementById('easy-dialogs-root')
}

function getSessionStorage() {
	try {
		const storage = window.sessionStorage
		const probeKey = `${STORAGE_PREFIX}probe`

		storage.setItem(probeKey, '1')
		storage.removeItem(probeKey)

		return storage
	} catch (error) {
		return null
	}
}

function getTargetContainer() {
	return (
		document.querySelector('#content') ||
		document.querySelector('main') ||
		document.querySelector('.wp-site-blocks') ||
		document.body
	)
}

function getDialogShowCount(storage, dialogId) {
	if (!storage || !dialogId) {
		return 0
	}

	const count = storage.getItem(`${STORAGE_PREFIX}${dialogId}`)
	return count ? parseInt(count, 10) : 0
}

function incrementDialogShowCount(storage, dialogId) {
	if (!storage || !dialogId) {
		return
	}

	const currentCount = getDialogShowCount(storage, dialogId)
	storage.setItem(`${STORAGE_PREFIX}${dialogId}`, String(currentCount + 1))
}

function getAllDialogShowCounts(storage) {
	if (!storage) {
		return {}
	}

	const counts = {}

	for (let index = 0; index < storage.length; index += 1) {
		const key = storage.key(index)

		if (!key || !key.startsWith(STORAGE_PREFIX)) {
			continue
		}

		const dialogId = key.slice(STORAGE_PREFIX.length)
		const value = parseInt(storage.getItem(key) || '0', 10)

		if (!dialogId || Number.isNaN(value)) {
			continue
		}

		counts[dialogId] = value
	}

	return counts
}

function applyDialogShowCounts(storage, nextCounts) {
	if (!storage || !nextCounts || typeof nextCounts !== 'object') {
		return
	}

	Object.entries(nextCounts).forEach(([dialogId, rawCount]) => {
		const incomingCount = Number(rawCount)

		if (!dialogId || Number.isNaN(incomingCount) || incomingCount < 0) {
			return
		}

		const currentCount = getDialogShowCount(storage, dialogId)
		if (incomingCount > currentCount) {
			storage.setItem(
				`${STORAGE_PREFIX}${dialogId}`,
				String(incomingCount)
			)
		}
	})
}

function getLocalStorage() {
	try {
		const storage = window.localStorage
		const probeKey = `${STORAGE_PREFIX}local-probe`

		storage.setItem(probeKey, '1')
		storage.removeItem(probeKey)

		return storage
	} catch (error) {
		return null
	}
}

function parseSyncPayload(value) {
	if (!value) {
		return null
	}

	try {
		return JSON.parse(value)
	} catch (error) {
		return null
	}
}

function setupSyncSessionResponder(storage) {
	const localStorage = getLocalStorage()

	if (!storage || !localStorage) {
		return
	}

	window.addEventListener('storage', (event) => {
		if (event.key !== SYNC_REQUEST_KEY || !event.newValue) {
			return
		}

		const payload = parseSyncPayload(event.newValue)
		if (!payload || payload.tabId === TAB_ID || !payload.requestId) {
			return
		}

		const response = JSON.stringify({
			type: 'sync-response',
			requestId: payload.requestId,
			tabId: TAB_ID,
			counts: getAllDialogShowCounts(storage),
			timestamp: Date.now()
		})

		localStorage.setItem(SYNC_RESPONSE_KEY, response)
		localStorage.removeItem(SYNC_RESPONSE_KEY)
	})
}

function syncSessionsAcrossTabs(storage) {
	const localStorage = getLocalStorage()

	if (!storage || !localStorage) {
		return Promise.resolve()
	}

	return new Promise((resolve) => {
		const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`
		let didFinish = false

		const complete = () => {
			if (didFinish) {
				return
			}

			didFinish = true
			window.removeEventListener('storage', onStorage)
			resolve()
		}

		const onStorage = (event) => {
			if (event.key !== SYNC_RESPONSE_KEY || !event.newValue) {
				return
			}

			const payload = parseSyncPayload(event.newValue)
			if (
				!payload ||
				payload.requestId !== requestId ||
				payload.tabId === TAB_ID
			) {
				return
			}

			applyDialogShowCounts(storage, payload.counts)
		}

		window.addEventListener('storage', onStorage)

		const request = JSON.stringify({
			type: 'sync-request',
			requestId,
			tabId: TAB_ID,
			timestamp: Date.now()
		})

		localStorage.setItem(SYNC_REQUEST_KEY, request)
		localStorage.removeItem(SYNC_REQUEST_KEY)

		window.setTimeout(complete, SYNC_WAIT_MS)
	})
}

function pushDataLayerEvent(eventName, payload = {}) {
	if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
		return
	}

	window.dataLayer.push({
		event: eventName,
		plugin: 'easy-dialogs',
		...payload
	})
}

function getDialogTrackingData(dialog, storage) {
	const dialogId = dialog.dataset.easyDialogId || ''
	const delaySeconds = Math.max(
		0,
		Number(dialog.dataset.easyDialogDelay || 0)
	)
	const showOnce = dialog.dataset.easyDialogShowOnce === '1'

	return {
		dialog_id: dialogId,
		dialog_frequency: showOnce ? 'once' : 'always',
		dialog_delay_seconds: delaySeconds,
		dialog_show_count: getDialogShowCount(storage, dialogId)
	}
}

function bindCloseButton(dialog) {
	const closeButton = dialog.querySelector('[data-easy-dialog-close]')

	if (closeButton) {
		closeButton.addEventListener('click', () => {
			dialog.close('close-button')
		})
	}
}

function bindBackdropClose(dialog) {
	dialog.addEventListener('click', (event) => {
		const dialogBounds = dialog.getBoundingClientRect()
		const isInsideDialog =
			event.clientX >= dialogBounds.left &&
			event.clientX <= dialogBounds.right &&
			event.clientY >= dialogBounds.top &&
			event.clientY <= dialogBounds.bottom

		if (!isInsideDialog) {
			dialog.close('backdrop')
		}
	})
}

function focusDialog(dialog) {
	// Move initial focus to the dialog itself so keyboard users start at the modal container.
	window.setTimeout(() => {
		if (!dialog.open) {
			return
		}

		if (!dialog.hasAttribute('tabindex')) {
			dialog.setAttribute('tabindex', '-1')
		}

		dialog.focus({ preventScroll: true })
	}, 0)
}

function rememberDialogOpener(dialog) {
	const activeElement = document.activeElement

	if (activeElement instanceof HTMLElement) {
		dialogOpeners.set(dialog, activeElement)
	}
}

function restoreDialogOpener(dialog) {
	const opener = dialogOpeners.get(dialog)
	dialogOpeners.delete(dialog)

	if (!(opener instanceof HTMLElement) || !opener.isConnected) {
		return
	}

	window.setTimeout(() => {
		opener.focus({ preventScroll: true })
	}, 0)
}

function openDialog(dialog, storage) {
	if (dialog.open) {
		return
	}

	const dialogId = dialog.dataset.easyDialogId
	rememberDialogOpener(dialog)

	// Increment count in both modes (only-show-once and show-every-page-visit)
	incrementDialogShowCount(storage, dialogId)

	try {
		if (typeof dialog.showModal === 'function') {
			dialog.showModal()
			focusDialog(dialog)
			pushDataLayerEvent(
				'easy_dialog_open',
				getDialogTrackingData(dialog, storage)
			)
			return
		}
	} catch (error) {
		// Fall back to the open attribute if showModal cannot run.
	}

	dialog.setAttribute('open', 'open')
	focusDialog(dialog)
	pushDataLayerEvent(
		'easy_dialog_open',
		getDialogTrackingData(dialog, storage)
	)
}

function initializeDialog(dialog, targetContainer, storage) {
	const dialogId = dialog.dataset.easyDialogId || ''
	const delaySeconds = Math.max(
		0,
		Number(dialog.dataset.easyDialogDelay || 0)
	)
	const showOnce = dialog.dataset.easyDialogShowOnce === '1'

	// Check if should skip (only for show-once mode): if count >= 1, already shown
	if (showOnce && getDialogShowCount(storage, dialogId) >= 1) {
		pushDataLayerEvent('easy_dialog_suppressed', {
			...getDialogTrackingData(dialog, storage),
			suppression_reason: 'session_show_once'
		})
		return
	}

	targetContainer.appendChild(dialog)
	bindCloseButton(dialog)
	bindBackdropClose(dialog)
	dialog.addEventListener('close', () => {
		restoreDialogOpener(dialog)
		pushDataLayerEvent('easy_dialog_close', {
			...getDialogTrackingData(dialog, storage),
			close_reason: dialog.returnValue || 'dismiss'
		})
	})

	if (delaySeconds > 0) {
		window.setTimeout(
			() => openDialog(dialog, storage),
			delaySeconds * 1000
		)
		return
	}

	openDialog(dialog, storage)
}

function initializeDialogs(storage = getSessionStorage()) {
	const root = getDialogRoot()

	if (!root) {
		return
	}

	const targetContainer = getTargetContainer()
	const dialogs = root.querySelectorAll('[data-easy-dialog]')

	dialogs.forEach((dialog) =>
		initializeDialog(dialog, targetContainer, storage)
	)
	root.remove()
}

const dialogSessionStorage = getSessionStorage()
setupSyncSessionResponder(dialogSessionStorage)
const initialSyncPromise = syncSessionsAcrossTabs(dialogSessionStorage)

function initializeDialogsAfterSync() {
	initialSyncPromise.finally(() => {
		initializeDialogs(dialogSessionStorage)
	})
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initializeDialogsAfterSync, {
		once: true
	})
} else {
	initializeDialogsAfterSync()
}
